import { useStore } from '@nanostores/solid'
import { decodeImage, Fit, Layout, Rive } from '@rive-app/canvas-lite'
import { useQueryClient } from '@tanstack/solid-query'
import { createSignal, onCleanup, onMount, Show } from 'solid-js'
import coinPng from '~/assets/figma/coin.png'
import gemPng from '~/assets/figma/gem.png'
import stampSvg from '~/assets/figma/stamp.svg'
import coinBlankWebp from '~/assets/coin-blank.webp'
import coinWebp from '~/assets/coin.webp'
import gemBlankWebp from '~/assets/gem-blank.webp'
import gemWebp from '~/assets/gem.webp'
import stampBlankWebp from '~/assets/stamp-blank.webp'
import stampWebp from '~/assets/stamp.webp'
import rewardRiv from '~/assets/rive/reward.riv'
import { animateCount } from '~/lib/animate-count'
import type { CurrencyKind, CurrencyReward } from '~/lib/currency-reward'
import {
  flyingStampsFrom,
  STAMP_FLIGHT_MS,
  STAMP_TOTAL_MS,
  type FlyingStamp,
  type StampAnchor,
  type StampPoint,
} from '~/lib/flying-stamps'
import { m } from '~/paraglide/messages'
import {
  adjustActivityTokenBalance,
  useActivityTokenBalance,
} from '~/resources/activity-token-balance'
import { adjustUserCurrency, useUser } from '~/resources/user'
import {
  $boostingPackReward,
  closeBoostingPackReward,
} from '~/store/boosting-pack-reward'
import { FlyingIcons } from './flying-icons'
import { CurrencyPill } from './top-nav'

const SM = 'State Machine 1'

// Per-currency artwork fed into the reward Rive (same `Plus Tree` artboard the
// stamp-shop tree redemption uses). `full` lands in imgReward (the coloured
// reveal), `blank` in imgRewardBlank (the soft silhouette behind it) — mirroring
// how getRewardTreeAssets feeds the tree's full/blank pair. All are 480px webp,
// matching the tree slot so the reward fills it (Rive draws a bound image at its
// source resolution, not scaled to the slot).
const REWARD_IMAGES: Record<CurrencyKind, { full: string; blank: string }> = {
  cookies: { full: stampWebp, blank: stampBlankWebp },
  coins: { full: coinWebp, blank: coinBlankWebp },
  gems: { full: gemWebp, blank: gemBlankWebp },
}

async function loadDecodedImage(url: string) {
  const res = await fetch(url)
  return decodeImage(new Uint8Array(await res.arrayBuffer()))
}

function viewport() {
  return { width: window.innerWidth, height: window.innerHeight }
}

function anchorOf(el: Element | undefined): StampAnchor | null {
  if (!el) return null
  const r = el.getBoundingClientRect()
  return { left: r.left, top: r.top, width: r.width, height: r.height }
}

const ICONS: Record<CurrencyKind, string> = {
  cookies: stampSvg,
  coins: coinPng,
  gems: gemPng,
}

function labelFor(reward: CurrencyReward) {
  const number = String(reward.amount)
  if (reward.kind === 'coins') return `${m.coin()} x ${number}`
  if (reward.kind === 'gems') return `${m.time_crystal()} x ${number}`
  // The activity token is surfaced as "stamps" everywhere else in the campaign.
  return m.stamps_text({ number })
}

// ~80% through the flight is when an icon visually meets the pill (per the
// stampFly keyframes); pop and start counting a beat after that so the number
// jumps in sync with the first impact. Mirrors reward-page.tsx.
const ARRIVAL_MS = Math.round(STAMP_FLIGHT_MS * 0.8)

export function BoostingPackRewardQueue() {
  const rewards = useStore($boostingPackReward)
  return (
    <Show when={rewards()}>{(list) => <RewardQueue rewards={list()} />}</Show>
  )
}

// Plays the reward Rive for a single currency. Re-mounted (keyed on kind) for
// each prize the queue steps through, so the Rive is built fresh and torn down
// per reward rather than re-feeding images into a long-lived instance.
function RewardRive(props: {
  kind: CurrencyKind
  onCanvas: (el: HTMLCanvasElement) => void
}) {
  let canvas: HTMLCanvasElement | undefined
  let rive: Rive | undefined
  let onResize: (() => void) | undefined
  const refs: { unref(): void }[] = []

  onMount(() => {
    if (!canvas) return
    const { full, blank } = REWARD_IMAGES[props.kind]

    rive = new Rive({
      canvas,
      src: rewardRiv,
      artboard: 'Plus Tree',
      stateMachines: [SM],
      autoBind: true,
      autoplay: true,
      shouldDisableRiveListeners: true,
      layout: new Layout({ fit: Fit.Contain }),
      onLoad: async () => {
        if (!rive) return
        rive.resizeDrawingSurfaceToCanvas()
        onResize = () => rive?.resizeDrawingSurfaceToCanvas()
        window.addEventListener('resize', onResize)

        const vmi = rive.viewModelInstance
        if (!vmi) return

        const num = vmi.number('numRewardType')
        if (num) num.value = 1

        const fullImg = await loadDecodedImage(full)
        vmi.image('imgReward')!.value = fullImg
        refs.push(fullImg)

        const blankImg = await loadDecodedImage(blank)
        vmi.image('imgRewardBlank')!.value = blankImg
        refs.push(blankImg)
      },
    })

    onCleanup(() => {
      rive?.cleanup()
      if (onResize) window.removeEventListener('resize', onResize)
      for (const ref of refs) ref.unref()
    })
  })

  return (
    <canvas
      ref={(el) => {
        canvas = el
        props.onCanvas(el)
      }}
      class="absolute left-1/2 top-0 h-full w-screen -translate-x-1/2"
      aria-hidden="true"
    />
  )
}

// Inner component so all per-session state (index, displayed balances) is
// seeded fresh from the current cache each time the queue opens and torn down
// on close.
function RewardQueue(props: { rewards: CurrencyReward[] }) {
  const queryClient = useQueryClient()
  const balanceQuery = useActivityTokenBalance()
  const userQuery = useUser()

  const [index, setIndex] = createSignal(0)
  const [busy, setBusy] = createSignal(false)
  const [flying, setFlying] = createSignal<FlyingStamp[]>([])
  const [flyingIcon, setFlyingIcon] = createSignal(stampSvg)

  const displayed: Record<CurrencyKind, [() => number, (v: number) => void]> = {
    cookies: createSignal(balanceQuery.data?.balance ?? 0),
    coins: createSignal(userQuery.data?.coin ?? 0),
    gems: createSignal(userQuery.data?.gem ?? 0),
  }

  const current = () => props.rewards[index()]
  const total = () => props.rewards.length

  let canvasEl: HTMLCanvasElement | undefined
  const pillEls: Partial<Record<CurrencyKind, HTMLDivElement>> = {}
  const timers: number[] = []
  onCleanup(() => timers.forEach(clearTimeout))

  const pillCenter = (kind: CurrencyKind): StampPoint => {
    const vp = viewport()
    const el = pillEls[kind]
    if (!el) return { x: vp.width / 2, y: 60 }
    const r = el.getBoundingClientRect()
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 }
  }

  const triggerPillPop = (kind: CurrencyKind) => {
    const el = pillEls[kind]
    if (!el) return
    el.classList.remove('animate-stamp-pill-pop')
    void el.offsetWidth
    el.classList.add('animate-stamp-pill-pop')
  }

  const advance = () => {
    setFlying([])
    if (index() < total() - 1) {
      setIndex(index() + 1)
      setBusy(false)
    } else {
      closeBoostingPackReward()
    }
  }

  const handleClaim = () => {
    if (busy()) return
    setBusy(true)
    const { kind, amount } = current()

    // Optimistically mirror the grant into the cache so the top-nav pills (which
    // sit behind this screen) already show the new totals when the queue closes.
    if (kind === 'cookies') {
      adjustActivityTokenBalance(queryClient, amount)
    } else {
      adjustUserCurrency(queryClient, kind === 'coins' ? 'coin' : 'gem', amount)
    }

    const [getDisplayed, setDisplayed] = displayed[kind]
    const from = getDisplayed()

    const stamps = flyingStampsFrom(
      anchorOf(canvasEl),
      pillCenter(kind),
      viewport(),
    )
    setFlyingIcon(ICONS[kind])
    setFlying(stamps)
    for (const stamp of stamps) {
      timers.push(
        window.setTimeout(() => triggerPillPop(kind), stamp.delay + ARRIVAL_MS),
      )
    }
    timers.push(
      window.setTimeout(
        () => animateCount({ from, delta: amount, onTick: setDisplayed }),
        ARRIVAL_MS,
      ),
    )
    timers.push(window.setTimeout(advance, STAMP_TOTAL_MS))
  }

  return (
    <div
      class="fixed inset-0 z-[60] flex flex-col pt-[100px]"
      style={{
        background:
          'radial-gradient(106.67% 55.25% at 50.13% 44.75%, #FFE8CB 24.52%, #EFA4B6 55.77%, #926FB0 100%)',
      }}
    >
      <div
        class="pointer-events-none fixed inset-x-0 top-0 z-10 flex justify-end gap-1 px-2"
        style={{ 'padding-top': 'max(var(--safe-area-top, 0px), 36px)' }}
      >
        <div ref={(el) => (pillEls.cookies = el)} class="py-2">
          <CurrencyPill icon={stampSvg} value={displayed.cookies[0]()} />
        </div>
        <div ref={(el) => (pillEls.coins = el)} class="py-2">
          <CurrencyPill icon={coinPng} value={displayed.coins[0]()} />
        </div>
        <div ref={(el) => (pillEls.gems = el)} class="py-2">
          <CurrencyPill icon={gemPng} value={displayed.gems[0]()} />
        </div>
      </div>

      <div class="flex flex-1 flex-col gap-6 px-6 pt-10 text-center">
        <h1 class="text-xl font-semibold text-white">{m.claimed_title()}</h1>

        <div class="relative h-[50vh]">
          <Show when={current().kind} keyed>
            {(kind) => (
              <RewardRive kind={kind} onCanvas={(el) => (canvasEl = el)} />
            )}
          </Show>
        </div>

        <p class="text-[26px] font-bold text-white">{labelFor(current())}</p>
        <p class="text-sm font-semibold tabular-nums text-white/80">
          {index() + 1}/{total()}
        </p>
      </div>

      <div class="flex flex-col px-6 pb-12 pt-4">
        <button
          type="button"
          disabled={busy()}
          class="mx-auto h-[52px] w-full max-w-[640px] rounded-xl bg-white px-4 font-bold text-[#176361] shadow-[0px_0px_20px_6px_rgba(223,221,255,0.5)] transition active:bg-[#e6e6e6] active:shadow-none disabled:opacity-70 disabled:shadow-none"
          onClick={handleClaim}
        >
          {m.cart_claim_active()}
        </button>
      </div>

      <FlyingIcons stamps={flying()} icon={flyingIcon()} />
    </div>
  )
}
